import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { zIndexStickyLayer3 } from '@xing-com/layout-tokens';
import { ProgressBar } from '@xing-com/progress-bar';
import {
  space4XL,
  spaceS,
  spaceXS,
  xdlColorBackground,
  xdlColorChart01,
} from '@xing-com/tokens';
import { BodyCopy } from '@xing-com/typography';

const Container = styled.div`
  padding: ${space4XL} ${spaceXS} ${spaceS};
  position: sticky;
  top: 0px;
  background: ${xdlColorBackground};
  z-index: ${zIndexStickyLayer3}; // let the step content scroll below but keep the modal X button above
`;

const Caption = styled(BodyCopy)`
  padding-top: ${spaceS};

  &:focus {
    outline: none;
  }
`;

const REVIEW_MODE_PROGRESS_PERCENTAGE = 98;

type WizardProgressBarProps = {
  currentStep: number;
  totalSteps: number;
  isReviewMode: boolean;
};

export const WizardProgressBar: React.FC<WizardProgressBarProps> = ({
  currentStep,
  totalSteps,
  isReviewMode,
}) => {
  const [progress, setProgress] = useState(0);
  const captionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setProgress(
      isReviewMode
        ? REVIEW_MODE_PROGRESS_PERCENTAGE
        : Math.round((currentStep / totalSteps) * 100) || 100
    );
  }, [currentStep, totalSteps, isReviewMode]);

  useEffect(() => {
    // move the focus from next button to the first item on the view on step change
    if (captionRef.current) {
      captionRef.current.focus();
    }
  }, [currentStep]);

  return (
    <Container>
      <ProgressBar
        progress={progress}
        showLabel={false}
        progressBarColor={xdlColorChart01}
        data-testid="wizard-progress-bar"
      />
      <Caption
        size="small"
        sizeConfined="medium"
        noMargin
        tabIndex={-1}
        ref={captionRef}
      >
        {isReviewMode ? (
          <FormattedMessage id="JOBS_APPLY_WIZARD_PROGRESS_BAR_CAPTION_REVIEW" />
        ) : (
          <>
            {currentStep === 1 ? (
              <>
                <FormattedMessage id="JOBS_APPLY_WIZARD_PROGRESS_BAR_CAPTION" />
                {' - '}
              </>
            ) : null}
            <FormattedMessage
              id="JOBS_APPLY_WIZARD_PROGRESS_BAR_STEP"
              values={{ currentStep, totalSteps }}
            />
          </>
        )}
      </Caption>
    </Container>
  );
};
